'use client';

import { FlywayFilterCarousel } from '@/components/patterns/FlywayFilterCarousel';
import { OutboundTextLink } from '@/components/patterns/OutboundTextLink';
import { GA_LINK_PLACEMENT } from '@/constants/analytics';
import {
  STATE_CHART_DEFAULT_FLYWAY_FILTER,
  STATE_FLYWAY_COLORS,
  STATE_FLYWAY_FILTERS,
  type StateChartViewMode,
  type StateFlywayFilterId,
} from '@/constants/stateCharts';

import styles from './StateChartControls.module.css';

import type { Messages } from '@/content/messages';

type StateChartControlsProps = {
  heading: string;
  charts: Messages['state']['charts'];
  datazoneUrl: string;
  /** Intent / toggle pressed state. */
  viewMode: StateChartViewMode;
  /**
   * Painted chart mode — drives xl+ `panelOverlay` so the shell matches the
   * chart still exiting (lags `viewMode` until exit completes).
   */
  layoutViewMode: StateChartViewMode;
  flywayFilter: StateFlywayFilterId;
  onViewModeChange: (mode: StateChartViewMode) => void;
  onFlywayFilterChange: (filter: StateFlywayFilterId) => void;
  onClearSelection: () => void;
};

function toggleViewMode(
  current: StateChartViewMode,
  next: Exclude<StateChartViewMode, 'default'>,
): StateChartViewMode {
  return current === next ? 'default' : next;
}

function carouselColors(filter: StateFlywayFilterId) {
  if (filter === STATE_CHART_DEFAULT_FLYWAY_FILTER) {
    return {
      background: 'var(--color-black-3)',
      foreground: 'var(--text-primary-inverse)',
    };
  }

  return {
    background: STATE_FLYWAY_COLORS[filter],
    foreground: 'var(--color-ink)',
  };
}

/**
 * Interactive View-by + flyway filter panel for State donut/bar charts.
 * Renders a controls stack: filter panel + sticky carousel (compact) /
 * desktop tabs. Parent sticky scope must wrap this + the chart column.
 * Clicking an active View-by button deselects it and returns to the default donut.
 * “Clear selection” resets view mode + flyway filter (Figma Filter Panel).
 */
export function StateChartControls({
  heading,
  charts,
  datazoneUrl,
  viewMode,
  layoutViewMode,
  flywayFilter,
  onViewModeChange,
  onFlywayFilterChange,
  onClearSelection,
}: StateChartControlsProps) {
  /*
   * Compact: wrap panel + sticky carousel so View-by → Show ALL uses Figma’s
   * 12px gap (971:56747), not mainRow’s larger chart gap. Overlay class lives
   * on the stack so xl+ donut still takes the panel out of flow as one unit.
   */
  const stackClass =
    layoutViewMode === 'default'
      ? `${styles.controlsStack} ${styles.panelOverlay}`
      : styles.controlsStack;

  return (
    <div className={stackClass}>
      <div className={styles.panel}>
        <h3 className={styles.heading}>{heading}</h3>

        <div className={styles.group}>
          <p className={styles.label}>{charts.viewByLabel}</p>
          <div className={styles.toggleRow} role="group" aria-label={charts.viewByLabel}>
            <button
              type="button"
              className={
                viewMode === 'speciesGroup' ? styles.toggleActive : styles.toggleInactive
              }
              aria-pressed={viewMode === 'speciesGroup'}
              onClick={() =>
                onViewModeChange(toggleViewMode(viewMode, 'speciesGroup'))
              }
            >
              <span
                className={`${styles.toggleIcon} ${styles.iconSpecies}`}
                aria-hidden
              />
              {charts.viewBySpeciesGroup}
            </button>
            <button
              type="button"
              className={
                viewMode === 'habitat' ? styles.toggleActive : styles.toggleInactive
              }
              aria-pressed={viewMode === 'habitat'}
              onClick={() => onViewModeChange(toggleViewMode(viewMode, 'habitat'))}
            >
              <span
                className={`${styles.toggleIcon} ${styles.iconHabitat}`}
                aria-hidden
              />
              {charts.viewByHabitat}
            </button>
          </div>
        </div>

        <div className={styles.desktopFilters}>
          <div className={styles.group}>
            <p className={styles.label}>{charts.filterByLabel}</p>
            <ul className={styles.filterList} role="group" aria-label={charts.filterByLabel}>
              {STATE_FLYWAY_FILTERS.map((filterId, index) => {
                const label = charts.filters[index] ?? filterId;
                const isActive = flywayFilter === filterId;
                const flywayColor =
                  filterId === STATE_CHART_DEFAULT_FLYWAY_FILTER
                    ? undefined
                    : STATE_FLYWAY_COLORS[filterId];

                return (
                  <li key={filterId}>
                    <button
                      type="button"
                      className={isActive ? styles.filterTabActive : styles.filterTab}
                      aria-pressed={isActive}
                      style={
                        isActive && flywayColor
                          ? {
                              backgroundColor: flywayColor,
                              color: 'var(--color-ink)',
                            }
                          : undefined
                      }
                      onClick={() => onFlywayFilterChange(filterId)}
                    >
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <button
            type="button"
            className={styles.clearSelection}
            onClick={onClearSelection}
          >
            {charts.clearSelection}
          </button>

          <OutboundTextLink
            href={datazoneUrl}
            className={styles.datazoneLink}
            linkPlacement={GA_LINK_PLACEMENT.datazoneChart}
          >
            {charts.datazoneLink}
          </OutboundTextLink>
        </div>
      </div>

      <FlywayFilterCarousel
        filters={STATE_FLYWAY_FILTERS}
        value={flywayFilter}
        onChange={onFlywayFilterChange}
        labels={charts.filters}
        previousLabel={charts.previousFlyway}
        nextLabel={charts.nextFlyway}
        getColors={carouselColors}
        variant="inverse"
        className={styles.carousel}
      />
    </div>
  );
}
